import { createClient } from './supabase';
import type { User } from '@lingaai/types';
import { generateWorkspaceSlug, generateWorkspaceEmail } from '@lingaai/shared-utils';

// Type-safe window access that works in both browser and Node.js
declare const window: { location?: { origin?: string } } | undefined;

export async function signIn(email: string, password: string) {
  const supabase = createClient();
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });

  if (error) throw error;
  return data;
}

export async function signUp(params: { email: string; password: string; firstName: string; lastName: string; organization: string }) {
  const supabase = createClient();

  // Step 1: Create auth user FIRST
  const { data, error } = await supabase.auth.signUp({
    email: params.email,
    password: params.password,
  });

  if (error) throw error;
  if (!data.user) throw new Error('User creation failed');

  // Step 2: Extract domain from email
  const domain = params.email.split('@')[1]?.toLowerCase();
  if (!domain) throw new Error('Invalid email address');

  // Step 3: Look up tenant by website domain
  const { data: existingTenant } = await supabase.from('tenants').select('id, workspace_slug').eq('website_domain', domain).single();

  let tenantId: string | null = null;
  let userStatus: 'pending' | 'approved' = 'approved';

  if (existingTenant) {
    // Scenario A: Known tenant (domain matches) - auto-approve
    tenantId = existingTenant.id;
    userStatus = 'approved';
  } else {
    // Scenario B or C: New domain
    const slug = generateWorkspaceSlug(params.organization);

    // Check if slug already exists (collision detection)
    const { data: slugExists } = await supabase.from('tenants').select('id').eq('workspace_slug', slug).single();

    if (slugExists) {
      // Scenario C: Slug collision (suspect) - create user without tenant, pending approval
      tenantId = null;
      userStatus = 'pending';
    } else {
      // Scenario B: New tenant - create it and auto-approve
      const { data: newTenant, error: tenantError } = await supabase
        .from('tenants')
        .insert({
          name: params.organization,
          workspace_slug: slug,
          workspace_email: generateWorkspaceEmail(slug),
          website_domain: domain,
          subscription_user_id: data.user.id, // Set first user as subscription owner
        })
        .select('id')
        .single();

      if (tenantError) throw tenantError;
      if (!newTenant) throw new Error('Failed to create tenant');

      tenantId = newTenant.id;
      userStatus = 'approved';
    }
  }

  // Step 4: Insert user profile data
  const { error: profileError } = await supabase.from('users').insert({
    id: data.user.id,
    tenant_id: tenantId,
    email: params.email,
    first_name: params.firstName,
    last_name: params.lastName,
    organization: params.organization,
    full_name: `${params.firstName} ${params.lastName}`,
    role: 'admin',
    status: userStatus,
  });

  if (profileError) throw profileError;

  // Step 5: Create CRM lead (only if approved and has tenant)
  if (userStatus === 'approved' && tenantId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        await supabase.functions.invoke('manage-lead', {
          body: {
            action: 'create',
            personData: {
              email: params.email,
              first_name: params.firstName,
              last_name: params.lastName,
              organization: params.organization,
            },
          },
        });
      }
    } catch (leadError) {
      // Log but don't fail signup if CRM lead creation fails
      console.error('Failed to create CRM lead:', leadError);
    }
  }

  // TODO: If status is 'pending', send email to tenant admins

  return data;
}

export async function signOut() {
  const supabase = createClient();
  const { error } = await supabase.auth.signOut();

  if (error) throw error;
}

export async function getCurrentUser(): Promise<User | null> {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return null;

  const { data: userData } = await supabase.from('users').select('*, tenants!inner(module_config)').eq('id', user.id).single();

  return userData as User;
}

export async function getSession() {
  const supabase = createClient();
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session;
}

export async function signInWithGoogle() {
  const supabase = createClient();
  const redirectTo = typeof window !== 'undefined' && typeof window.location !== 'undefined' ? `${window.location.origin}/home` : '/home';

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
    },
  });

  if (error) throw error;
  return data;
}
